import { isReconnectZap } from 'BMapsSrc/model/zapReason';
import { campaignStatusLabel } from '../../../gibbs/autonomousCampaign/narrative/campaignArtifacts';
import { WorkflowState } from './constants';

export const INITIAL_CAMPAIGN_PANEL_STATE = Object.freeze({
    events: Object.freeze([]),
    running: false,
    escalation: null,
});

const CAMPAIGN_HEADER_MESSAGE_LIMIT = 96;
const MASKED_PARENT_RE = /^COMPOUND_[A-Z]+$/;
const SUBMITTED_STEP_RE = /^submitted-step-(\d+)$/;

export function completedCampaignStepIndex(actions = [], executionStates = {}, record = {}) {
    if (
        record.actionCompleted !== true
        || record.preActionReason
        || record.actionId === 'campaign-cancel'
    ) return -1;
    const submittedStepMatch = typeof record.workflowStepId === 'string'
        ? SUBMITTED_STEP_RE.exec(record.workflowStepId)
        : null;
    if (submittedStepMatch) {
        const index = Number(submittedStepMatch[1]) - 1;
        return index >= 0 && index < actions.length && actions[index]?.actionId === record.actionId
            ? index
            : -1;
    }
    return actions.findIndex((action, index) => (
        action.actionId === record.actionId
        && executionStates[index]?.status !== 'SUCCEEDED'
    ));
}

function appendEvent(events, event) {
    return [...events, event];
}

function directionFromAtomIds(atomUniqueIds) {
    return {
        fromAtomUniqueId: atomUniqueIds[0],
        toAtomUniqueId: atomUniqueIds[1],
    };
}

function selectedGrowVectorForReply(input) {
    if (Array.isArray(input)) {
        const atomUniqueIds = input.filter((id) => typeof id === 'string' && id.length > 0).slice(0, 2);
        if (atomUniqueIds.length !== 2) return null;
        return {
            valid: true,
            atomUniqueIds,
            direction: directionFromAtomIds(atomUniqueIds),
        };
    }
    if (!input || typeof input !== 'object' || input.valid !== true) return null;
    const atomUniqueIds = Array.isArray(input.atomUniqueIds)
        ? input.atomUniqueIds.filter((id) => typeof id === 'string' && id.length > 0).slice(0, 2)
        : [];
    if (atomUniqueIds.length !== 2) return null;
    const direction = input.direction && typeof input.direction === 'object'
        && input.direction.fromAtomUniqueId === atomUniqueIds[0]
        && input.direction.toAtomUniqueId === atomUniqueIds[1]
        ? {
            fromAtomUniqueId: atomUniqueIds[0],
            toAtomUniqueId: atomUniqueIds[1],
        }
        : directionFromAtomIds(atomUniqueIds);
    const parentId = typeof input.parentId === 'string' && MASKED_PARENT_RE.test(input.parentId)
        ? input.parentId
        : null;
    return {
        valid: true,
        atomUniqueIds,
        direction,
        ...(parentId ? { parentId } : {}),
    };
}

function sharedReuseSelectedVector(vectorEvidence) {
    if (!vectorEvidence) return null;
    return {
        valid: true,
        atomUniqueIds: vectorEvidence.atomUniqueIds,
        direction: vectorEvidence.direction,
    };
}

function truncateHeaderMessage(message) {
    if (typeof message !== 'string' || message.length === 0) return '';
    const chars = Array.from(message);
    if (chars.length <= CAMPAIGN_HEADER_MESSAGE_LIMIT) return message;
    return `${chars.slice(0, CAMPAIGN_HEADER_MESSAGE_LIMIT - 1).join('')}…`;
}

function latestEvent(events, predicate) {
    for (let i = events.length - 1; i >= 0; i -= 1) {
        if (predicate(events[i])) return events[i];
    }
    return null;
}

// One mapping, shared with the panel's completion block and the session
// narrative list, which used to print the engine's status word raw.
function statusLabelForComplete(status) {
    return campaignStatusLabel(status) || 'Complete';
}

/**
 * Short reason to show on a step a terminal campaign never finished.
 *
 * The step card renders this inline, so it has to stay one line. The full
 * campaign narrative is reachable from the campaign panel and its download —
 * putting it here instead spilled the whole markdown document into every
 * unfinished step's error caption.
 *
 * @param {string} status - terminal campaign status
 * @returns {string}
 */
export function campaignStepFailureReason(status) {
    if (status === 'escalated') {
        return 'Campaign stopped at a checkpoint. See the campaign narrative.';
    }
    if (status === 'budget-exceeded') {
        return 'Campaign stopped: signed budget exhausted. See the campaign narrative.';
    }
    if (status === 'failed' || status === 'error') {
        return 'Campaign failed. See the campaign narrative.';
    }
    return `Campaign ${statusLabelForComplete(status).toLowerCase()}. See the campaign narrative.`;
}

function statusLabelForStatus(status) {
    if (status === 'restoring') return 'Restoring';
    if (status === 'resumed') return 'Resumed';
    if (status === 'recovery-required') return 'Recovery';
    if (status === 'disabled') return 'Disabled';
    if (status === 'unavailable') return 'Unavailable';
    if (status === 'rejected') return 'Rejected';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'error' || status === 'failed') return 'Error';
    if (status === 'complete') return 'Complete';
    if (status === 'requested' || status === 'running') return 'Running';
    return 'Ready';
}

/**
 * One neutral line describing how this workspace's crystal ligands are roled.
 *
 * Deliberately not a warning. An unmarked crystal ligand is the reviewed setup in
 * workspaces A and B — a run that docks it is the signed behavior — so raising an
 * alarm would fire on most of the benchmark set and advise a change that breaks
 * it. This states what the run will treat as what and stops there; a reader who
 * knows the molecule is a cofactor draws their own conclusion.
 *
 * @param {{marked?: string[], unmarked?: string[]}} roles - from crystalLigandRoleSummary
 * @returns {string|null} null when no crystal ligand is loaded
 */
export function campaignReferenceRoleSummary(roles = {}) {
    const marked = Array.isArray(roles.marked) ? roles.marked : [];
    const unmarked = Array.isArray(roles.unmarked) ? roles.unmarked : [];
    if (!marked.length && !unmarked.length) return null;
    const parts = [];
    if (marked.length) {
        parts.push(`Scored reference: ${marked.join(', ')}.`);
    }
    if (unmarked.length) {
        parts.push(
            `${marked.length ? 'Not marked as a reference' : 'No reference marked'}: `
            + `${unmarked.join(', ')} — came with the protein structure, and this campaign will `
            + `dock ${unmarked.length === 1 ? 'it' : 'them'} as a candidate.`,
        );
    }
    return parts.join(' ');
}

/**
 * Does this `zapAll` mean the workflow pane should drop what it is holding?
 *
 * A zap normally does mean that: the workspace the plan was written against is
 * gone. A reconnect is the exception. `reconnectAndRestoreSession` zaps only so
 * it can restore the state it captured a moment earlier, and a campaign does not
 * live in that captured state at all — it lives in the Orchestrator, which
 * persists it and replays it. So clearing the pane on a reconnect destroys the
 * one copy of the plan, the step states and the narrative the browser held,
 * while the run itself carries on unseen.
 *
 * Deliberately narrow: only a campaign is spared. A plain workflow's results
 * hold references to compounds the restore is about to replace, so a stale
 * display would be worse than an empty one, and its executor cannot survive the
 * connection going away.
 *
 * @param {{reason?: import('BMapsSrc/model/zapReason').ZapReasonValue}} [zapEventData]
 *   the `zapAll` payload
 * @param {{holdsCampaign?: boolean}} [paneState] whether the pane holds a campaign plan
 * @returns {boolean}
 */
export function zapClearsWorkflowPane(zapEventData, { holdsCampaign = false } = {}) {
    if (!holdsCampaign) return true;
    return !isReconnectZap(zapEventData);
}

/**
 * Was a campaign submission caught mid-signing, so that a reconnect has to
 * revoke it?
 *
 * `SUBMITTED` with no digest yet is the window between the pane committing to
 * submit and the signed submission reaching the Orchestrator: the workspace
 * baseline was checked before the await, the signing is still running, and
 * nothing server-side knows about this campaign. A reconnect through that window
 * would sign against the workspace that just went away and submit into the one
 * being rebuilt.
 *
 * With a digest, the campaign is already the Orchestrator's and must be left
 * alone — revoking it here would be exactly the "cancel as a side effect of
 * reconnecting" this whole change exists to remove.
 *
 * @param {{workflowState?: string, campaignDigest?: string|null}} [paneState]
 * @returns {boolean}
 */
export function reconnectRevokesCampaignSubmission({
    workflowState, campaignDigest = null,
} = {}) {
    return workflowState === WorkflowState.SUBMITTED && !campaignDigest;
}

export function campaignPanelSummary(panelState = INITIAL_CAMPAIGN_PANEL_STATE) {
    const events = Array.isArray(panelState?.events) ? panelState.events : [];
    const escalation = panelState?.escalation || null;
    const latestStatus = latestEvent(events, (event) => event?.type === 'campaign-status');
    const latestTerminal = latestEvent(events, (event) => event?.type === 'campaign-complete');

    if (escalation) {
        const recovery = escalation?.prompt?.kind === 'action-recovery';
        return {
            status: recovery ? 'recovery-required' : 'checkpoint',
            label: recovery ? 'Recovery' : 'Checkpoint',
            message: recovery
                ? 'Interrupted action requires an explicit retry or stop decision.'
                : 'Waiting for Workflow Checker confirmation.',
            tone: 'warning',
            active: true,
        };
    }

    if (panelState?.running === true) {
        const resumed = latestStatus?.status === 'resumed' || latestStatus?.status === 'restoring';
        return {
            status: resumed ? latestStatus.status : 'running',
            label: resumed ? statusLabelForStatus(latestStatus.status) : 'Running',
            message: truncateHeaderMessage(latestStatus?.message || 'Campaign is running.'),
            tone: 'info',
            active: true,
        };
    }

    if (latestTerminal) {
        const label = statusLabelForComplete(latestTerminal.status);
        let tone = 'success';
        if (label === 'Error') {
            tone = 'error';
        } else if (label === 'Stopped' || label === 'Cancelled') {
            tone = 'warning';
        }
        return {
            status: latestTerminal.status || 'complete',
            label,
            message: latestTerminal.narrative ? 'Narrative available for review.' : `Campaign ${label.toLowerCase()}.`,
            tone,
            active: false,
        };
    }

    if (latestStatus) {
        const label = statusLabelForStatus(latestStatus.status);
        return {
            status: latestStatus.status || 'ready',
            label,
            message: truncateHeaderMessage(latestStatus.message || `Campaign ${latestStatus.status || 'ready'}.`),
            tone: ['Disabled', 'Unavailable', 'Rejected', 'Error'].includes(label)
                ? 'error'
                : 'default',
            active: false,
        };
    }

    return {
        status: 'ready',
        label: 'Ready',
        message: 'Campaign ready.',
        tone: 'default',
        active: false,
    };
}

export function reduceCampaignPanelState(prevState, event = {}) {
    const prev = prevState || INITIAL_CAMPAIGN_PANEL_STATE;
    if (
        event.type === 'decision-record'
        && event.record?.id
        && (prev.events || []).some((candidate) => (
            candidate?.type === 'decision-record'
            && candidate?.record?.id === event.record.id
        ))
    ) {
        return prev;
    }
    if (event.type === 'campaign-status') {
        const running = event.status === 'requested' || event.status === 'running'
            ? true
            : !['complete', 'disabled', 'unavailable', 'rejected', 'cancelled'].includes(event.status)
                && prev.running;
        return {
            events: appendEvent(prev.events || [], event),
            running,
            escalation: prev.escalation || null,
        };
    }
    if (event.type === 'escalation') {
        return {
            events: prev.events || [],
            running: true,
            escalation: event,
        };
    }
    if (event.type === 'decision-record' && event.record?.actionId === 'campaign-resume') {
        return {
            events: appendEvent(appendEvent(prev.events || [], {
                type: 'campaign-status',
                status: 'resumed',
                message: event.record.rationale || 'Campaign resumed from persisted state.',
            }), event),
            running: true,
            escalation: prev.escalation || null,
        };
    }
    if (event.type === 'campaign-complete') {
        return {
            events: appendEvent(prev.events || [], event),
            running: false,
            escalation: null,
        };
    }
    return {
        events: appendEvent(prev.events || [], event),
        running: prev.running === true,
        escalation: prev.escalation || null,
    };
}

export function campaignEscalationReply(decision, selectedGrowVector = null, checkpoint = null) {
    const requestedInput = typeof checkpoint === 'string'
        ? checkpoint
        : checkpoint?.requestedInput || null;
    const eligibleParentIds = Array.isArray(checkpoint?.eligibleParentIds)
        ? checkpoint.eligibleParentIds.filter((id) => typeof id === 'string' && id.length > 0)
        : [];
    const vectorPolicy = typeof checkpoint?.vectorPolicy === 'string'
        ? checkpoint.vectorPolicy
        : null;
    const applySharedVectorToAll = checkpoint?.applySharedVectorToAll === true;
    const reviewsRecordedExclusion = checkpoint?.kind
        === 'post-minimize-exclusion-continuation';
    const explicitParentVectorResolutions = Array.isArray(checkpoint?.parentVectorResolutions)
        ? checkpoint.parentVectorResolutions
        : null;
    const vectorEvidence = selectedGrowVectorForReply(selectedGrowVector);
    const ids = vectorEvidence?.atomUniqueIds || [];
    const usesSuggestedBondVector = requestedInput === 'suggested-bond-vector';
    const usesMultiParentVectors = requestedInput === 'multi-parent-bond-vectors';
    const usesSequentialParentVector = requestedInput === 'selected-bond-vector'
        && typeof checkpoint?.targetParentId === 'string';
    const recommendedDecision = checkpoint?.decision
        && typeof checkpoint.decision === 'object'
        ? checkpoint.decision
        : null;
    const recommendedAction = typeof recommendedDecision?.action === 'string'
        ? recommendedDecision.action
        : null;
    const recommendedNextActionId = typeof recommendedDecision?.nextActionId === 'string'
        ? recommendedDecision.nextActionId
        : null;
    const selectedNextActionId = typeof checkpoint?.nextActionId === 'string'
        ? checkpoint.nextActionId
        : recommendedNextActionId;
    let rationale = reviewsRecordedExclusion
        ? 'Workflow Checker stopped the campaign after reviewing the recorded post-minimize exclusion.'
        : 'Workflow Checker stopped the campaign at the interactive checkpoint.';
    if (decision === 'branch') {
        rationale = selectedNextActionId
            ? `Workflow Checker accepted the recommended branch to ${selectedNextActionId}.`
            : 'Workflow Checker accepted the recommended branch.';
    } else if (decision === 'retry') {
        rationale = 'Workflow Checker accepted the recommended retry.';
    } else if (decision === 'proceed') {
        if (reviewsRecordedExclusion) {
            rationale = 'Workflow Checker continued the campaign after reviewing the recorded post-minimize exclusion.';
        } else if (usesSequentialParentVector && explicitParentVectorResolutions) {
            const skipped = explicitParentVectorResolutions
                .filter((entry) => entry?.status === 'skipped').length;
            rationale = skipped > 0
                ? `Workflow Checker recorded ${skipped} remaining parent${skipped === 1 ? '' : 's'} skipped.`
                : 'Workflow Checker approved the interactive campaign checkpoint with a selected bond vector.';
        } else if (usesMultiParentVectors && explicitParentVectorResolutions) {
            const applied = explicitParentVectorResolutions.filter((entry) => entry?.status === 'applied').length;
            const skipped = explicitParentVectorResolutions.filter((entry) => entry?.status === 'skipped').length;
            rationale = 'Workflow Checker approved the interactive campaign checkpoint '
                + `with ${applied} parent vector${applied === 1 ? '' : 's'} applied`
                + ` and ${skipped} parent${skipped === 1 ? '' : 's'} skipped.`;
        } else if (usesMultiParentVectors && vectorEvidence && applySharedVectorToAll) {
            rationale = 'Workflow Checker approved the interactive campaign checkpoint '
                + 'with a selected grow vector for multi-parent reuse.';
        } else if (usesSuggestedBondVector) {
            rationale = 'Workflow Checker approved the interactive campaign checkpoint '
                + 'with a Deputy-suggested bond vector.';
        } else if (
            requiresSelectedVector(requestedInput)
            && !usesMultiParentVectors
            && vectorEvidence
        ) {
            rationale = 'Workflow Checker approved the interactive campaign checkpoint '
                + 'with a selected bond vector.';
        } else if (recommendedAction === 'branch') {
            rationale = selectedNextActionId
                ? `Workflow Checker declined the recommended branch to ${selectedNextActionId} and continued the reviewed workflow.`
                : 'Workflow Checker declined the recommended branch and continued the reviewed workflow.';
        } else if (recommendedAction === 'retry') {
            rationale = 'Workflow Checker declined the recommended retry and continued the reviewed workflow.';
        } else {
            rationale = 'Workflow Checker approved the interactive campaign checkpoint.';
        }
    }
    const reply = {
        decision,
        decider: 'human',
        rationale,
    };
    if (decision === 'branch' && selectedNextActionId) {
        reply.nextActionId = selectedNextActionId;
    }
    if (
        decision === 'proceed'
        && usesSequentialParentVector
        && explicitParentVectorResolutions
    ) {
        const resolutions = explicitParentVectorResolutions
            .filter((entry) => entry && typeof entry.parentId === 'string')
            .map((entry) => {
                if (entry.status === 'skipped') {
                    return {
                        parentId: entry.parentId,
                        source: 'user-skipped',
                        status: 'skipped',
                    };
                }
                const atomUniqueIds = Array.isArray(entry.atomUniqueIds)
                    ? entry.atomUniqueIds.slice(0, 2)
                    : ids.slice(0, 2);
                const selected = sharedReuseSelectedVector({
                    ...(vectorEvidence || {}),
                    valid: true,
                    atomUniqueIds,
                });
                return {
                    parentId: entry.parentId,
                    source: entry.source || 'parent-specific-user',
                    status: 'applied',
                    atomUniqueIds,
                    selectedGrowVector: { ...selected, parentId: entry.parentId },
                };
            });
        const applied = resolutions.find((entry) => entry.status === 'applied');
        reply.params = {
            ...(applied ? {
                atomUniqueIds: applied.atomUniqueIds,
                selectedGrowVector: applied.selectedGrowVector,
            } : {}),
            parentVectorResolutions: resolutions,
        };
    } else if (
        decision === 'proceed'
        && usesMultiParentVectors
        && explicitParentVectorResolutions
    ) {
        reply.params = {
            vectorPolicy: vectorPolicy || 'shared-reuse-with-parent-fallback',
            parentVectorResolutions: explicitParentVectorResolutions
                .filter((entry) => entry && typeof entry.parentId === 'string')
                .map((entry) => {
                    if (entry.status === 'skipped') {
                        return {
                            parentId: entry.parentId,
                            source: 'user-skipped',
                            status: 'skipped',
                        };
                    }
                    const atomUniqueIds = Array.isArray(entry.atomUniqueIds)
                        ? entry.atomUniqueIds.slice(0, 2)
                        : ids.slice(0, 2);
                    const selected = sharedReuseSelectedVector({
                        ...(vectorEvidence || {}),
                        valid: true,
                        atomUniqueIds,
                    });
                    return {
                        parentId: entry.parentId,
                        source: entry.source || 'parent-specific-user',
                        status: 'applied',
                        atomUniqueIds,
                        selectedGrowVector: selected,
                    };
                }),
        };
    } else if (
        decision === 'proceed'
        && usesMultiParentVectors
        && vectorEvidence
        && applySharedVectorToAll
    ) {
        reply.params = {
            vectorPolicy: vectorPolicy || 'shared-reuse-with-parent-fallback',
            parentVectorResolutions: eligibleParentIds.map((parentId) => ({
                parentId,
                source: 'shared-reused',
                status: 'applied',
                atomUniqueIds: ids.slice(0, 2),
                selectedGrowVector: sharedReuseSelectedVector(vectorEvidence),
            })),
        };
    } else if (decision === 'proceed' && usesSuggestedBondVector) {
        reply.params = { useSuggestedBondVector: true };
    } else if (
        decision === 'proceed'
        && requiresSelectedVector(requestedInput)
        && !usesMultiParentVectors
        && vectorEvidence
    ) {
        reply.params = {
            atomUniqueIds: ids.slice(0, 2),
            selectedGrowVector: vectorEvidence,
        };
    }
    return reply;
}

function requiresSelectedVector(requestedInput) {
    return requestedInput === 'selected-bond-vector' || requestedInput === 'multi-parent-bond-vectors';
}
